import {
  Star,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const EDGE_TONES = {
  "+EV": "border-[#00D4FF]/40 text-[#00D4FF] bg-[#00D4FF]/5",
  "-EV": "border-red-500/40 text-red-400 bg-red-500/5",
  NEUTRAL: "border-zinc-700 text-zinc-400 bg-zinc-900/40",
};

const EDGE_ICONS = {
  "+EV": TrendingUp,
  "-EV": TrendingDown,
  NEUTRAL: Minus,
};

function Stars({ n }) {
  return (
    <div className="flex gap-0.5" data-testid="research-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          strokeWidth={1.5}
          className={i <= n ? "fill-[#00D4FF] text-[#00D4FF]" : "text-zinc-700"}
        />
      ))}
    </div>
  );
}

function EdgeBadge({ label, value }) {
  const key = EDGE_ICONS[label] ? label : "NEUTRAL";
  const Icon = EDGE_ICONS[key];
  const tone = EDGE_TONES[key];
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border ${tone} mono text-[11px] tracking-wider`}
      data-testid="research-edge-badge"
    >
      <Icon size={12} strokeWidth={1.75} />
      {label}
      {Number.isFinite(value) && (
        <span className="opacity-80">
          {value > 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`}
        </span>
      )}
    </div>
  );
}

function SimulationBar({ home, away, homeName, awayName }) {
  const total = (home || 0) + (away || 0) || 1;
  const homePct = (home / total) * 100;
  const awayPct = (away / total) * 100;
  return (
    <div data-testid="research-sim-bar">
      <div className="flex justify-between mono text-[11px] mb-1.5">
        <span className="text-zinc-400">
          {awayName || "Away"} <span className="text-[#00D4FF]">{away}</span>
        </span>
        <span className="text-zinc-400">
          <span className="amber-text">{home}</span> {homeName || "Home"}
        </span>
      </div>
      <div className="flex h-2 border border-zinc-800 overflow-hidden">
        <div
          className="bg-[#00D4FF]/70"
          style={{ width: `${awayPct}%` }}
          title={`${awayName} wins ${away}/1000`}
        />
        <div
          className="bg-[#FFBF00]/70"
          style={{ width: `${homePct}%` }}
          title={`${homeName} wins ${home}/1000`}
        />
      </div>
      <div className="flex justify-between mono text-[10px] mt-1 text-zinc-500">
        <span>{awayPct.toFixed(1)}%</span>
        <span>{homePct.toFixed(1)}%</span>
      </div>
    </div>
  );
}

function MetricBox({ label, value, hint, testid }) {
  return (
    <div className="border border-zinc-800 p-3" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-display text-xl mt-1">{value}</div>
      {hint && <div className="mono text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function fmtAmerican(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const v = Math.round(n);
  return v > 0 ? `+${v}` : `${v}`;
}

function VerdictHeader({ analysis, edge }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-800 pb-5">
      <div className="flex-1 min-w-0">
        <div className="overline neon-text mb-2">RESEARCH VERDICT</div>
        <h3 className="font-display text-3xl lg:text-4xl leading-tight">
          <span className="text-[#00D4FF]" data-testid="research-lean">
            {analysis.lean || "Pass"}
          </span>
        </h3>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">{analysis.verdict}</p>
      </div>
      <div className="text-right space-y-2">
        <div className="overline">CONFIDENCE</div>
        <Stars n={analysis.confidence || 0} />
        <EdgeBadge label={edge.edge_label || "NEUTRAL"} value={edge.edge_pct} />
      </div>
    </div>
  );
}

function SimulationSection({ sim, homeName, awayName, dataQuality }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-[#00D4FF]" strokeWidth={1.5} />
        <div className="overline">1,000-GAME SIMULATION</div>
      </div>
      <SimulationBar
        home={sim.home_wins || 0}
        away={sim.away_wins || 0}
        homeName={homeName}
        awayName={awayName}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <MetricBox
          label="MODEL · AWAY"
          value={`${(sim.model_away_win_pct ?? 0).toFixed(1)}%`}
          hint={awayName}
          testid="research-model-away"
        />
        <MetricBox
          label="MODEL · HOME"
          value={`${(sim.model_home_win_pct ?? 0).toFixed(1)}%`}
          hint={homeName}
          testid="research-model-home"
        />
        <MetricBox
          label="AVG MARGIN"
          value={sim.avg_margin_for_winner || "—"}
          hint="for the winning side"
          testid="research-avg-margin"
        />
        <MetricBox
          label="DATA QUALITY"
          value={(dataQuality || "medium").toUpperCase()}
          hint="based on context provided"
          testid="research-data-quality"
        />
      </div>
    </div>
  );
}

function FairLineSection({ fair, mkt, homeName, awayName }) {
  return (
    <div>
      <div className="overline mb-3">FAIR LINE vs DRAFTKINGS</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="border border-zinc-800 p-4">
          <div className="overline">{awayName}</div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="mono text-xs text-zinc-500">Fair</span>
            <span className="font-display text-2xl text-[#00D4FF]">
              {fmtAmerican(fair.away_american)}
            </span>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="mono text-xs text-zinc-500">DK</span>
            <span className="mono text-sm">{fmtAmerican(mkt.dk_away_american)}</span>
          </div>
        </div>
        <div className="border border-zinc-800 p-4">
          <div className="overline">{homeName}</div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="mono text-xs text-zinc-500">Fair</span>
            <span className="font-display text-2xl amber-text">
              {fmtAmerican(fair.home_american)}
            </span>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="mono text-xs text-zinc-500">DK</span>
            <span className="mono text-sm">{fmtAmerican(mkt.dk_home_american)}</span>
          </div>
        </div>
      </div>
      {fair.method && (
        <p className="mono text-[11px] text-zinc-500 mt-3">{fair.method}</p>
      )}
    </div>
  );
}

function FactorsAndRisks({ factors, risks }) {
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div>
        <div className="overline mb-3 flex items-center gap-2">
          <CheckCircle2 size={12} className="text-[#00D4FF]" /> KEY FACTORS
        </div>
        <ul className="space-y-2" data-testid="research-key-factors">
          {factors.map((f) => (
            <li key={f} className="flex gap-2 text-sm text-zinc-300">
              <span className="text-[#00D4FF] mono shrink-0">▸</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="overline mb-3 flex items-center gap-2">
          <AlertTriangle size={12} className="amber-text" /> RISK FLAGS
        </div>
        <ul className="space-y-2" data-testid="research-risks">
          {risks.map((r) => (
            <li key={r} className="flex gap-2 text-sm text-zinc-300">
              <span className="amber-text mono shrink-0">!</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ViabilityHeading({ isViable }) {
  if (isViable) {
    return (
      <div className="flex items-center gap-2 font-display text-xl">
        <CheckCircle2 size={18} className="text-[#00D4FF]" />
        <span className="text-[#00D4FF]">VIABLE</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 font-display text-xl">
      <XCircle size={18} className="text-red-400" />
      <span className="text-red-400">NOT RECOMMENDED</span>
    </div>
  );
}

function MartingaleViability({ mart }) {
  return (
    <div className="border border-zinc-800 p-5 bg-zinc-950/60">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-1">MARTINGALE STEP-1 FILTER</div>
          <ViabilityHeading isViable={!!mart.is_viable_step_1} />
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">{mart.reasoning}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="overline">BASE STAKE</div>
          <div className="font-display text-xl">
            {mart.recommended_base_stake_pct ?? "—"}%
          </div>
          <div className="overline mt-2">MAX STEPS</div>
          <div className="font-display text-xl">{mart.max_steps_suggested ?? "—"}</div>
        </div>
      </div>
    </div>
  );
}

export default function ResearchResultCard({ result }) {
  const a = result.analysis || {};
  const sim = a.simulation || {};
  const mkt = a.market_edge || {};
  const fair = a.fair_line || {};
  const mart = a.martingale_viability || {};
  const homeName = result.game?.home?.name || "Home";
  const awayName = result.game?.away?.name || "Away";

  return (
    <div
      className="border border-zinc-800 bg-zinc-950/40 p-6 lg:p-8 space-y-6"
      data-testid="research-result"
    >
      <VerdictHeader analysis={a} edge={mkt} />
      <SimulationSection
        sim={sim}
        homeName={homeName}
        awayName={awayName}
        dataQuality={a.data_quality}
      />
      <FairLineSection fair={fair} mkt={mkt} homeName={homeName} awayName={awayName} />
      <FactorsAndRisks factors={a.key_factors || []} risks={a.risks || []} />
      <MartingaleViability mart={mart} />
      {a.research_summary && (
        <div className="border-l-2 border-[#00D4FF] pl-4 text-sm text-zinc-300 leading-relaxed">
          {a.research_summary}
        </div>
      )}
    </div>
  );
}
